import React from 'react';
import ReactFlatpickr from 'react-flatpickr';
import { createGlobalStyle } from 'styled-components';
import 'flatpickr/dist/themes/light.css';
import 'flatpickr/dist/plugins/confirmDate/confirmDate.css';

const FLATPICKR_HOOKS = [
  'onChange',
  'onOpen',
  'onClose',
  'onMonthChange',
  'onYearChange',
  'onReady',
  'onValueUpdate',
  'onDayCreate',
];

/**
 *
 * @param {import('react-flatpickr').DateTimePickerProps} props
 */
export function Flatpickr(props) {
  const ref = React.useRef();
  const locale = React.useContext(FlatpickrContext);

  // Why this wrapper is more involved than a `<ReactFlatpickr {...props} />`:
  //
  // react-flatpickr's `mergeHooks` rebuilds the hook arrays on EVERY render, so
  // whenever a hook (e.g. `onChange`) is passed as a prop, its
  // `componentDidUpdate` always believes the hook changed and calls
  // `flatpickr.set('onChange', …)`. flatpickr's `set()` ALWAYS runs `redraw()`,
  // which rebuilds the calendar's day cells. If that happens while the calendar
  // is open, the day element under the user's cursor is replaced and the click
  // is swallowed — the long-standing "have to click twice to pick a date" bug.
  //
  // componentDidUpdate has TWO independent redraw paths and BOTH must be
  // neutralised: (1) the option/hook `set()` loop — fixed by handing hooks to
  // flatpickr as REFERENTIALLY STABLE arrays carried inside `options` (not as
  // props) and stabilising the options object; and (2) a separate
  // `if (props.value !== prevProps.value) flatpickr.setDate(value, false)`
  // branch (setDate also always redraws) — fixed by stabilising the `value`
  // prop further down. With both no-ops on churn renders, the open calendar is
  // never rebuilt under the cursor, so the first click always lands.

  // Latest handler per hook, captured without changing downstream identity.
  const handlersRef = React.useRef({});
  FLATPICKR_HOOKS.forEach(name => {
    const fromProps = props[name];
    const fromOptions = props.options && props.options[name];
    handlersRef.current[name] =
      fromProps !== undefined ? fromProps : fromOptions;
  });

  // One stable array per hook, forwarding to the latest handler via the ref.
  // `onClose` also blurs the input: react-flatpickr adds that by default, but
  // that default is bypassed once we feed `onClose` through `options`.
  const stableHooks = React.useMemo(() => {
    const call = (handler, args) => {
      if (!handler) return;
      if (Array.isArray(handler)) handler.forEach(fn => fn && fn(...args));
      else handler(...args);
    };
    return FLATPICKR_HOOKS.reduce((acc, name) => {
      acc[name] = [
        (...args) => {
          if (name === 'onClose') {
            const fp = ref.current && ref.current.flatpickr;
            if (fp && fp.input && fp.input.blur) fp.input.blur();
          }
          call(handlersRef.current[name], args);
        },
      ];
      return acc;
    }, {});
  }, []);

  // Stabilise the options object by value (ignore function identity, compare
  // Dates by time) so a fresh inline `options={{…}}` each render doesn't make
  // react-flatpickr re-`set()` (and redraw) every option.
  const stableOptions = useStableValue(props.options);

  const options = React.useMemo(() => {
    const merged = { ...stableOptions, disableMobile: true };
    if (locale) {
      merged.locale = locale;
    }
    FLATPICKR_HOOKS.forEach(name => {
      if (
        name === 'onChange' ||
        name === 'onClose' ||
        handlersRef.current[name] != null
      ) {
        merged[name] = stableHooks[name];
      }
    });
    return merged;
  }, [stableOptions, locale, stableHooks]);

  // Force flatpickr to use 'en' language (can't handle time change with hindu numbers)
  React.useLayoutEffect(() => {
    const container =
      ref.current &&
      ref.current.flatpickr &&
      ref.current.flatpickr.calendarContainer;
    if (container) {
      container.setAttribute('lang', 'en');
    }
  }, []);

  // Hooks now live (stably) inside `options`; don't also pass them as props.
  const rest = { ...props };
  delete rest.options;
  FLATPICKR_HOOKS.forEach(name => delete rest[name]);

  // The second redraw path: react-flatpickr's componentDidUpdate runs
  //   if (props.value !== prevProps.value) flatpickr.setDate(props.value, false)
  // and flatpickr.setDate ALWAYS calls redraw() (rebuilds every day cell).
  // Consumers feed `value` via final-form `format={v => new Date(v)}`, a brand
  // new Date every render, so that branch fired a redraw on EVERY re-render —
  // which, when it lands between a day's mousedown and mouseup, destroys the day
  // node and swallows the click (the "click twice to pick a date" bug, since the
  // click listener is delegated on the days container). Passing a referentially
  // STABLE value — only changing when the underlying date(s) change — makes the
  // branch a no-op on churn re-renders. Called unconditionally (it's a hook);
  // only assigned back when `value` was actually provided.
  const stableValue = useStableDateValue(rest.value);
  if ('value' in rest) {
    rest.value = stableValue;
  }

  return <ReactFlatpickr {...rest} options={options} ref={ref} />;
}

// Referentially stable copy of a flatpickr `value`: returns the SAME reference
// across renders unless the underlying date(s) actually change. Handles
// null/undefined, Date, number, string and arrays (range / multiple) — so a
// parent passing `new Date(v)` (or an array of fresh Dates) each render no
// longer makes react-flatpickr's `value !== prevValue` setDate->redraw fire.
function useStableDateValue(value) {
  const ref = React.useRef(value);
  if (!isSameDateValue(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

function toComparable(v) {
  return v instanceof Date ? v.getTime() : v;
}

function isSameDateValue(a, b) {
  if (a === b) return true;
  const aArr = Array.isArray(a);
  const bArr = Array.isArray(b);
  if (aArr || bArr) {
    if (!aArr || !bArr || a.length !== b.length) return false;
    return a.every((v, i) => toComparable(v) === toComparable(b[i]));
  }
  return toComparable(a) === toComparable(b);
}

// Returns a referentially stable copy of `value` that only changes when its
// contents change. Function-valued keys are treated as equal (flatpickr option
// callbacks like formatDate are behaviourally stable but get a new identity
// each render); Dates are compared by time.
function useStableValue(value) {
  const ref = React.useRef(value);
  if (!isShallowEqualIgnoringFns(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

function isShallowEqualIgnoringFns(a, b) {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => {
    const av = a[key];
    const bv = b[key];
    if (av === bv) return true;
    if (typeof av === 'function' && typeof bv === 'function') return true;
    if (av instanceof Date && bv instanceof Date) {
      return av.getTime() === bv.getTime();
    }
    return false;
  });
}

Flatpickr.propTypes = ReactFlatpickr.propTypes;

export const FlatpickrContext = React.createContext(null);

// eslint-disable-next-line react/prop-types
export function FlatpickrProvider({ children, locale }) {
  return (
    <FlatpickrContext.Provider value={locale}>
      {children}
    </FlatpickrContext.Provider>
  );
}

export const FlatpickrStyles = createGlobalStyle`
  body {
    .flatpickr-calendar {
      box-shadow: 0 3px 13px rgba(0,0,0,0.2);
      &.arrowTop {
        &::before,
        &::after {
          border-bottom-color: white;
        }
      }
      &.arrowBottom {
        &::before,
        &::after {
          border-top-color: white;
        }
      }
    }
    .flatpickr-months .flatpickr-prev-month,
    .flatpickr-months .flatpickr-next-month,
    .flatpickr-months .flatpickr-month,
    .flatpickr-current-month {
      position: static;
    }

    .flatpickr-months .flatpickr-prev-month,
    .flatpickr-months .flatpickr-next-month {
      height: auto;
      & svg {
        vertical-align: middle;
      }
    }

    .flatpickr-months {
      align-items: center;
      padding-top: 8px;
      border-radius: 4px 4px 0 0;
      background: white;
    }

    .flatpickr-weekdays {
      background: white;
      margin: 8px 0;
    }

    span.flatpickr-weekday {
      background: white;
      font-weight: 300;
    }

    .flatpickr-months .flatpickr-month {
      background: white;
      height: auto;
      overflow: visible;
    }

    .flatpickr-current-month {
      padding: 0;
      width: auto;
      height: auto;
    }

    .flatpickr-day.selected,
    .flatpickr-day.startRange,
    .flatpickr-day.endRange,
    .flatpickr-day.selected.inRange,
    .flatpickr-day.startRange.inRange,
    .flatpickr-day.endRange.inRange,
    .flatpickr-day.selected:focus,
    .flatpickr-day.startRange:focus,
    .flatpickr-day.endRange:focus,
    .flatpickr-day.selected:hover,
    .flatpickr-day.startRange:hover,
    .flatpickr-day.endRange:hover,
    .flatpickr-day.selected.prevMonthDay,
    .flatpickr-day.startRange.prevMonthDay,
    .flatpickr-day.endRange.prevMonthDay,
    .flatpickr-day.selected.nextMonthDay,
    .flatpickr-day.startRange.nextMonthDay,
    .flatpickr-day.endRange.nextMonthDay {
      background: #fea201;
      border-color: #fea201;
    }

    .flatpickr-months .flatpickr-prev-month:hover svg,
    .flatpickr-months .flatpickr-next-month:hover svg {
      fill: #fea201;
    }

    .flatpickr-current-month .flatpickr-monthDropdown-months {
      height: auto;
      margin-right: 16px;
      background: white;
      border: 1px solid #eee;
    }

    .flatpickr-days,
    .flatpickr-innerContainer {
      border: none;
    }

    .flatpickr-innerContainer {
      border-radius: 0 0 4px 4px;
    }

    .flatpickr-calendar.showTimeInput.hasTime .flatpickr-innerContainer {
      border-radius: 0;
    }

    .flatpickr-calendar.showTimeInput.hasTime .flatpickr-time {
      border-radius: 0 0 4px 4px;
      border: none;
      border-top: 1px solid #eee;
    }

    /* Week/month picker styles */
    .flatpickr-day.week.selected {
      width: 48px;
      background: #ffd18d;
      border-color: transparent;
      box-shadow: -5px 0 0 #ffd18d, 5px 0 0 #ffd18d;
      font-weight: normal;
    }
    .flatpickr-day.week.selected,
    .flatpickr-monthSelect-month.selected {
      color: #484848;
    }
    .flatpickr-day.week.selected.today,
    .flatpickr-monthSelect-month.selected {
      background: rgb(254, 162, 1, 1);
    }
  }
`;
